import { router as inertiaRouter } from '@inertiajs/react';
import { clearUser } from '@/lib/analytics';
import { setSentryUser } from '@/lib/sentry';

// When someone signs out, nothing this tab kept may show the next person what
// they opened (#519). Inertia encrypts the pages it keeps in history.state
// with a key in this tab's session storage; this file drops that key, and
// everything else the tab kept, whenever the signed-in user changes.

const CHANNEL = 'bosko:session';
const SIGNED_OUT = 'signed-out';

// Whose pages this tab's history holds: the signed-in user's id, absent when
// nobody is signed in. Session storage, so it outlives a trip to Stripe or a
// sign-in provider in the same tab, which a variable wouldn't.
const OWNER_KEY = 'bosko:historyOwner';

const ownerOf = (page) => String(page?.props?.currentUser?.id ?? '');

function readOwner() {
  try {
    return window.sessionStorage.getItem(OWNER_KEY) ?? '';
  } catch {
    return '';
  }
}

function writeOwner(owner) {
  try {
    if (owner) window.sessionStorage.setItem(OWNER_KEY, owner);
    else window.sessionStorage.removeItem(OWNER_KEY);
  } catch {
    // Storage unavailable: the login page's clearHistory still applies.
  }
}

// Sign out from this tab. Forget the analytics and Sentry identity and drop
// the history key before the request leaves, so Back can't decrypt this
// user's pages even if the response never arrives.
export function signOut(logoutPath, router = inertiaRouter) {
  clearUser();
  setSentryUser(null);
  router.clearHistory();
  router.delete(logoutPath);
}

function openChannel() {
  return typeof window.BroadcastChannel === 'function'
    ? new window.BroadcastChannel(CHANNEL)
    : null;
}

// The ActionCable consumer identifies the user once, when it connects, and
// outlives a sign-out: the next user's subscriptions would run over the
// previous user's connection and receive their streams. The next subscription
// reconnects as whoever is signed in then.
function disconnectCable() {
  return import('@/lib/cable')
    .then(({ default: consumer }) => consumer.disconnect())
    .catch(() => {
      // An offline chunk load: there is no connection to close either.
    });
}

// A page that clears history and has nobody signed in: the login page, after a
// sign-out, an expired or revoked session, or an account deletion.
const endsSession = (page) =>
  Boolean(page?.clearHistory) && !page?.props?.currentUser;

// When the page's user isn't the one this tab's history belongs to, drop the
// history key. Returns whether it did.
function claimHistory(router, page) {
  const owner = ownerOf(page);
  if (readOwner() === owner) return false;
  router.clearHistory();
  writeOwner(owner);
  return true;
}

// Runs in setup(), before Inertia restores a page from history: on Back from
// another site, Inertia decrypts the stored entry and shows it in place of the
// server's page, so a key left from the previous user must already be gone.
//
// After that, whenever a page arrives for a different user (sign-out,
// sign-in, an ended session, an impersonation switch) the tab also flushes
// Inertia's prefetch cache, which keeps pages for up to a minute, and the live
// connection. A page that ends the session tells this browser's other Bosko
// tabs, whose keys are their own: each drops its key, and any still showing a
// signed-in page reloads onto the login page.
export function installSessionEndCleanup(
  router,
  initialPage,
  { channel = openChannel(), reload = () => window.location.reload() } = {},
) {
  let current = initialPage;
  claimHistory(router, initialPage);

  const arrive = (page) => {
    current = page;
    if (!claimHistory(router, page)) return;
    router.flushAll();
    disconnectCable();
  };

  router.on('navigate', (event) => arrive(event.detail.page));
  router.on('success', (event) => {
    const { page } = event.detail;
    arrive(page);
    if (endsSession(page)) channel?.postMessage(SIGNED_OUT);
  });

  if (endsSession(initialPage)) channel?.postMessage(SIGNED_OUT);

  if (channel) {
    channel.onmessage = (event) => {
      if (event.data !== SIGNED_OUT) return;
      // Even a tab showing a signed-out page can hold the previous user's
      // pages behind or ahead of it. None of this broadcasts, so tabs never
      // ping-pong.
      router.clearHistory();
      writeOwner('');
      router.flushAll();
      disconnectCable();
      if (current?.props?.currentUser) reload();
    };
  }
}
